import React, { useState, useCallback, useEffect } from 'react'
import {
  DocumentArrowUpIcon,
  CheckCircleIcon,
  XCircleIcon,
  ExclamationTriangleIcon,
  ClockIcon,
  InformationCircleIcon,
  BanknotesIcon,
  ScaleIcon
} from '@heroicons/react/24/outline'
import { useToast } from '@/hooks/useToast'
import { api } from '@/services/api'
import { useNavigate } from 'react-router-dom'

interface UploadResult {
  success: boolean
  filename: string
  accounts_found: number
  total_transactions: number
  transactions_imported: number
  date_range: {
    start_date?: string
    end_date?: string
  }
  details?: {
    total_transactions: number
    successful_transactions: number
    failed_transactions: number
    accounts_processed: number
  }
  error?: string
}

interface UploadFile {
  file: File
  status: 'pending' | 'uploading' | 'success' | 'error'
  progress: number
  result?: UploadResult
  error?: string
}

const OFXUploadPage: React.FC = () => {
  const [uploadFiles, setUploadFiles] = useState<UploadFile[]>([])
  const [isDragOver, setIsDragOver] = useState(false)
  const [selectedAccountId, setSelectedAccountId] = useState<number | undefined>()
  const [bankAccounts, setBankAccounts] = useState<any[]>([])
  const [loadingAccounts, setLoadingAccounts] = useState(true)
  const { success, error: showError } = useToast()
  const navigate = useNavigate()

  // Load bank accounts on component mount
  useEffect(() => {
    loadBankAccounts()
  }, [])

  const loadBankAccounts = async () => {
    try {
      setLoadingAccounts(true)
      // Buscar contas do sistema (Account), não BankAccount
      const response = await api.get('/accounts')
      console.log('Contas carregadas:', response.data)
      // API pode retornar array direto ou objeto com propriedade accounts
      const accounts = Array.isArray(response.data) ? response.data : (response.data.accounts || [])
      setBankAccounts(accounts)
    } catch (err: any) {
      console.error('Error loading bank accounts:', err)
      showError('Erro ao carregar contas bancárias')
    } finally {
      setLoadingAccounts(false)
    }
  }

  // Drag and Drop handlers
  const handleDragOver = useCallback((e: React.DragEvent) => {
    e.preventDefault()
    setIsDragOver(true)
  }, [])

  const handleDragLeave = useCallback((e: React.DragEvent) => {
    e.preventDefault()
    setIsDragOver(false)
  }, [])

  const handleDrop = useCallback((e: React.DragEvent) => {
    e.preventDefault()
    setIsDragOver(false)

    const files = Array.from(e.dataTransfer.files).filter(
      file => file.name.toLowerCase().endsWith('.ofx')
    )

    if (files.length === 0) {
      showError('Apenas arquivos .ofx são aceitos')
      return
    }

    addFilesToQueue(files)
  }, [])

  const handleFileInput = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files ? Array.from(e.target.files) : []
    addFilesToQueue(files)
    e.target.value = '' // Reset input
  }, [])

  const addFilesToQueue = (files: File[]) => {
    const newUploadFiles: UploadFile[] = files.map(file => ({
      file,
      status: 'pending',
      progress: 0
    }))

    setUploadFiles(prev => [...prev, ...newUploadFiles])
  }

  const uploadFile = async (uploadFile: UploadFile, index: number) => {
    const formData = new FormData()
    formData.append('file', uploadFile.file)

    // Construir URL com query param se account_id foi selecionado
    let uploadUrl = '/integrations/banking/ofx/upload'
    if (selectedAccountId) {
      uploadUrl += `?account_id=${selectedAccountId}`
    }

    try {
      // Update status to uploading
      setUploadFiles(prev => prev.map((item, i) =>
        i === index ? { ...item, status: 'uploading', progress: 10 } : item
      ))

      const response = await api.post<UploadResult>(
        uploadUrl,
        formData,
        {
          headers: {
            'Content-Type': 'multipart/form-data'
          },
          onUploadProgress: (progressEvent) => {
            const progress = progressEvent.total
              ? Math.round((progressEvent.loaded * 100) / progressEvent.total)
              : 50

            setUploadFiles(prev => prev.map((item, i) =>
              i === index ? { ...item, progress } : item
            ))
          }
        }
      )

      // Success
      setUploadFiles(prev => prev.map((item, i) =>
        i === index ? {
          ...item,
          status: 'success',
          progress: 100,
          result: response.data
        } : item
      ))

      success(`Arquivo ${uploadFile.file.name} processado com sucesso!`)

    } catch (error: any) {
      const errorMessage = error.response?.data?.detail || error.message || 'Erro ao processar arquivo'

      setUploadFiles(prev => prev.map((item, i) =>
        i === index ? {
          ...item,
          status: 'error',
          progress: 0,
          error: errorMessage
        } : item
      ))

      showError(`Erro ao processar ${uploadFile.file.name}: ${errorMessage}`)
    }
  }

  const startUpload = (index: number) => {
    const fileToUpload = uploadFiles[index]
    if (fileToUpload && fileToUpload.status === 'pending') {
      uploadFile(fileToUpload, index)
    }
  }

  const startAllUploads = () => {
    uploadFiles.forEach((fileItem, index) => {
      if (fileItem.status === 'pending') {
        setTimeout(() => startUpload(index), index * 500) // Stagger uploads
      }
    })
  }

  const removeFile = (index: number) => {
    setUploadFiles(prev => prev.filter((_, i) => i !== index))
  }

  const getStatusIcon = (status: UploadFile['status']) => {
    switch (status) {
      case 'pending':
        return <ClockIcon className="h-5 w-5 text-gray-400" />
      case 'uploading':
        return <div className="h-5 w-5 border-2 border-blue-500 border-t-transparent rounded-full animate-spin" />
      case 'success':
        return <CheckCircleIcon className="h-5 w-5 text-green-500" />
      case 'error':
        return <XCircleIcon className="h-5 w-5 text-red-500" />
    }
  }

  const getStatusColor = (status: UploadFile['status']) => {
    switch (status) {
      case 'pending':
        return 'bg-gray-100 border-gray-200'
      case 'uploading':
        return 'bg-blue-50 border-blue-200'
      case 'success':
        return 'bg-green-50 border-green-200'
      case 'error':
        return 'bg-red-50 border-red-200'
    }
  }

  const formatDate = (dateStr: string | undefined) => {
    if (!dateStr) return 'N/A'
    try {
      return new Date(dateStr).toLocaleDateString('pt-BR')
    } catch {
      return dateStr
    }
  }

  const formatFileSize = (bytes: number) => {
    if (bytes === 0) return '0 Bytes'
    const k = 1024
    const sizes = ['Bytes', 'KB', 'MB', 'GB']
    const i = Math.floor(Math.log(bytes) / Math.log(k))
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i]
  }

  return (
    <div className="p-6 space-y-6">
      {/* Header */}
      <div className="border-b border-gray-200 pb-4">
        <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
          <DocumentArrowUpIcon className="h-8 w-8 text-blue-600" />
          Upload de Extratos OFX
        </h1>
        <p className="text-gray-600 mt-2">
          Importe extratos bancários no formato OFX para sincronizar suas transações automaticamente
        </p>
      </div>

      {/* Info Alert */}
      <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
        <div className="flex">
          <InformationCircleIcon className="h-5 w-5 text-blue-400" />
          <div className="ml-3">
            <h3 className="text-sm font-medium text-blue-800">
              Como funciona
            </h3>
            <div className="mt-2 text-sm text-blue-700">
              <ul className="list-disc list-inside space-y-1">
                <li>Arraste arquivos .ofx para a área abaixo ou clique para selecionar</li>
                <li>Os extratos serão processados automaticamente</li>
                <li>Transações duplicadas são detectadas e ignoradas</li>
                <li>Você pode associar a uma conta específica (opcional)</li>
              </ul>
            </div>
          </div>
        </div>
      </div>

      {/* Account Selection - DESTACADO */}
      <div className="bg-gradient-to-r from-purple-50 to-blue-50 rounded-lg border-2 border-purple-300 p-6 shadow-sm">
        <div className="flex items-center gap-2 mb-3">
          <BanknotesIcon className="h-6 w-6 text-purple-600" />
          <label className="block text-base font-semibold text-gray-900">
            🏦 Selecione a Conta Bancária de Destino
          </label>
        </div>
        <p className="text-sm text-gray-600 mb-3">
          ⚠️ IMPORTANTE: Selecione a conta antes de importar para evitar criar contas duplicadas
        </p>
        <select
          value={selectedAccountId || ''}
          onChange={(e) => setSelectedAccountId(e.target.value ? Number(e.target.value) : undefined)}
          className="w-full px-4 py-3 text-base border-2 border-purple-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-purple-500 bg-white font-medium"
          disabled={loadingAccounts}
        >
          <option value="">
            {loadingAccounts ? '⏳ Carregando contas...' : '🔍 Detectar automaticamente (pode criar conta nova)'}
          </option>
          {bankAccounts.map(account => (
            <option key={account.id} value={account.id}>
              ✓ {account.name} - {account.bank || 'Banco não informado'}
              {account.balance !== undefined && ` - Saldo: R$ ${parseFloat(account.balance).toFixed(2)}`}
            </option>
          ))}
        </select>
        {!selectedAccountId && (
          <p className="text-xs text-orange-600 mt-2 font-medium">
            💡 Dica: Se você não selecionar uma conta, o sistema criará uma nova conta automaticamente
          </p>
        )}
        {selectedAccountId && (
          <p className="text-xs text-green-600 mt-2 font-medium">
            ✅ As transações serão importadas para a conta selecionada
          </p>
        )}
      </div>

      {/* Upload Area */}
      <div
        className={`border-2 border-dashed rounded-lg p-8 text-center transition-colors ${
          isDragOver
            ? 'border-blue-500 bg-blue-50'
            : 'border-gray-300 hover:border-gray-400'
        }`}
        onDragOver={handleDragOver}
        onDragLeave={handleDragLeave}
        onDrop={handleDrop}
      >
        <DocumentArrowUpIcon className="mx-auto h-12 w-12 text-gray-400 mb-4" />
        <div className="space-y-2">
          <p className="text-lg font-medium text-gray-900">
            Arraste arquivos .ofx aqui
          </p>
          <p className="text-gray-500">
            ou
          </p>
          <label className="inline-flex items-center px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 cursor-pointer transition-colors">
            <span>Selecionar arquivos</span>
            <input
              type="file"
              multiple
              accept=".ofx"
              className="hidden"
              onChange={handleFileInput}
            />
          </label>
        </div>
      </div>

      {/* Files List */}
      {uploadFiles.length > 0 && (
        <div className="space-y-4">
          <div className="flex items-center justify-between">
            <h3 className="text-lg font-medium text-gray-900">
              Arquivos ({uploadFiles.length})
            </h3>
            <button
              onClick={startAllUploads}
              disabled={uploadFiles.every(f => f.status !== 'pending')}
              className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:bg-gray-300 disabled:cursor-not-allowed transition-colors"
            >
              Processar Todos
            </button>
          </div>

          <div className="space-y-3">
            {uploadFiles.map((uploadFile, index) => (
              <div
                key={index}
                className={`p-4 rounded-lg border ${getStatusColor(uploadFile.status)}`}
              >
                <div className="flex items-center justify-between">
                  <div className="flex items-center space-x-3">
                    {getStatusIcon(uploadFile.status)}
                    <div>
                      <p className="font-medium text-gray-900">
                        {uploadFile.file.name}
                      </p>
                      <p className="text-sm text-gray-500">
                        {formatFileSize(uploadFile.file.size)} • {uploadFile.file.type || 'OFX'}
                      </p>
                    </div>
                  </div>

                  <div className="flex items-center space-x-3">
                    {uploadFile.status === 'pending' && (
                      <button
                        onClick={() => startUpload(index)}
                        className="px-3 py-1 bg-blue-600 text-white rounded text-sm hover:bg-blue-700"
                      >
                        Processar
                      </button>
                    )}
                    <button
                      onClick={() => removeFile(index)}
                      className="text-gray-400 hover:text-gray-600"
                    >
                      <XCircleIcon className="h-5 w-5" />
                    </button>
                  </div>
                </div>

                {/* Progress Bar */}
                {uploadFile.status === 'uploading' && (
                  <div className="mt-3">
                    <div className="bg-gray-200 rounded-full h-2">
                      <div
                        className="bg-blue-600 h-2 rounded-full transition-all duration-300"
                        style={{ width: `${uploadFile.progress}%` }}
                      />
                    </div>
                    <p className="text-xs text-gray-500 mt-1">
                      {uploadFile.progress}% processado
                    </p>
                  </div>
                )}

                {/* Success Details */}
                {uploadFile.status === 'success' && uploadFile.result && (
                  <div className="mt-3 p-3 bg-green-50 rounded-lg">
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
                      <div>
                        <p className="text-gray-500">Contas encontradas</p>
                        <p className="font-medium text-green-700">
                          {uploadFile.result.accounts_found}
                        </p>
                      </div>
                      <div>
                        <p className="text-gray-500">Total de transações</p>
                        <p className="font-medium text-green-700">
                          {uploadFile.result.total_transactions}
                        </p>
                      </div>
                      <div>
                        <p className="text-gray-500">Importadas</p>
                        <p className="font-medium text-green-700">
                          {uploadFile.result.transactions_imported}
                        </p>
                      </div>
                      <div>
                        <p className="text-gray-500">Período</p>
                        <p className="font-medium text-green-700">
                          {formatDate(uploadFile.result.date_range.start_date)} - {formatDate(uploadFile.result.date_range.end_date)}
                        </p>
                      </div>
                    </div>
                  </div>
                )}

                {/* Error Details */}
                {uploadFile.status === 'error' && (
                  <div className="mt-3 p-3 bg-red-50 rounded-lg">
                    <p className="text-sm text-red-700">
                      <ExclamationTriangleIcon className="h-4 w-4 inline mr-2" />
                      {uploadFile.error}
                    </p>
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Summary */}
      {uploadFiles.length > 0 && (
        <div className="bg-gray-50 rounded-lg p-4">
          <h4 className="font-medium text-gray-900 mb-2">Resumo</h4>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
            <div>
              <p className="text-gray-500">Total de arquivos</p>
              <p className="font-medium">{uploadFiles.length}</p>
            </div>
            <div>
              <p className="text-gray-500">Processados com sucesso</p>
              <p className="font-medium text-green-600">
                {uploadFiles.filter(f => f.status === 'success').length}
              </p>
            </div>
            <div>
              <p className="text-gray-500">Com erro</p>
              <p className="font-medium text-red-600">
                {uploadFiles.filter(f => f.status === 'error').length}
              </p>
            </div>
            <div>
              <p className="text-gray-500">Pendentes</p>
              <p className="font-medium text-yellow-600">
                {uploadFiles.filter(f => f.status === 'pending').length}
              </p>
            </div>
          </div>

          {uploadFiles.some(f => f.status === 'success') && (
            <div className="mt-3 pt-3 border-t border-gray-200">
              <p className="text-sm text-gray-500 mb-2">Total importado:</p>
              <div className="flex items-center justify-between">
                <div className="flex items-center space-x-2">
                  <BanknotesIcon className="h-4 w-4 text-green-500" />
                  <span className="text-sm font-medium">
                    {uploadFiles
                      .filter(f => f.result)
                      .reduce((sum, f) => sum + (f.result?.transactions_imported || 0), 0)
                    } transações
                  </span>
                </div>
                <button
                  onClick={() => navigate('/bank-statements')}
                  className="flex items-center space-x-2 px-4 py-2 bg-gradient-to-r from-green-600 to-emerald-600 text-white rounded-lg hover:from-green-700 hover:to-emerald-700 transition-all duration-200 transform hover:scale-105"
                >
                  <ScaleIcon className="h-4 w-4" />
                  <span className="font-medium">Classificar Transações</span>
                </button>
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  )
}

export default OFXUploadPage